import { useCallback } from "react";
import { useLoggedIn } from "../loginHelpers";
import { useAppContext } from "./useAppContext";
import { ActionType, type MctProvider } from "../../@types";
import { useQueryClient } from "@tanstack/react-query";
import {
  useBeneinfoProvidersMapnetQueryKey,
  useBeneinfoProvidersQueryKey,
} from "../query-hooks/queryKeyHooks";
import { useBeneinfoProviders } from "../query-hooks/pd/useBeninfoProviders";
import { useMapnetCoverage } from "../flag-hooks/useMapnetCoverage";
import { useGetBeneProvidersMapnet } from "../query-hooks/pd/useGetBeneProvidersMapnet";

/**
 * Returns a bene's saved providers and callbacks for adding and removing providers
 *
 * For authenticated benes, updates made to the cached queryClient value also
 * synchronizes with `AppState.providers`, since that's the context for the providers
 * referenced in the UI
 *
 * Other RQ hook values are also returned
 */
export const useProviders = () => {
  const { dispatch, state } = useAppContext();
  const { enableMapnetCoverage } = useMapnetCoverage(state.year);

  const isLoggedIn = useLoggedIn();
  const queryClient = useQueryClient();
  const beneinfoProvidersQueryKey = useBeneinfoProvidersQueryKey();
  const beneinfoProvidersMapnetQueryKey = useBeneinfoProvidersMapnetQueryKey();
  const savedProvidersQueryKey = enableMapnetCoverage
    ? beneinfoProvidersMapnetQueryKey
    : beneinfoProvidersQueryKey;

  // * Queries
  const beneinfoProvidersQuery = useBeneinfoProviders({
    disabled: enableMapnetCoverage,
  });
  const beneinfoProvidersMapnetQuery = useGetBeneProvidersMapnet();
  const {
    providers: beneinfoProviders,
    error,
    isError,
    isLoading,
  } = enableMapnetCoverage
    ? beneinfoProvidersMapnetQuery
    : beneinfoProvidersQuery;

  // * Constants
  const providers = isLoggedIn ? beneinfoProviders : state.anonProviders;

  // * Callbacks
  const addProvider = useCallback(
    (provider: MctProvider) => {
      if (isLoggedIn) {
        queryClient.setQueryData(
          savedProvidersQueryKey,
          (currentProviders: MctProvider[] = []) => [
            ...currentProviders,
            provider,
          ]
        );
      } else {
        dispatch({
          type: ActionType.ADD_PROVIDER,
          payload: provider,
        });
      }
    },
    [dispatch, isLoggedIn, queryClient, savedProvidersQueryKey]
  );
  const getProviderFromNpi = useCallback(
    (npi: string) => providers.find(p => p.npi === npi),
    [providers]
  );
  const removeProvider = useCallback(
    (provider: MctProvider) => {
      if (isLoggedIn) {
        queryClient.setQueryData(
          savedProvidersQueryKey,
          (currentProviders: MctProvider[] = []) =>
            currentProviders.filter(p => p.npi !== provider.npi)
        );
      } else {
        dispatch({
          type: ActionType.DELETE_PROVIDER,
          payload: `${provider.npi}`,
        });
      }
    },
    [dispatch, isLoggedIn, queryClient, savedProvidersQueryKey]
  );

  return {
    addProvider,
    error,
    getProviderFromNpi,
    isError,
    isLoading,
    providers,
    removeProvider,
  };
};
